import { getAppCta } from './navigation';
import type {
  CardTransaction,
  FaqContent,
  FeatureBlockContent,
  HeroContent,
  IconName,
  ImageRef,
  TextSectionContent,
} from './types';

/* Tech landing page copy (Figma "Tech Landing Page", 1362:65783 / mobile 1379:66484). */

export const techHero: HeroContent = {
  title: 'One card for your entire tech stack.',
  cta: { label: 'Get your nsave card', href: getAppCta.href },
  image: {
    src: '/images/tech/hero.jpg',
    webpSrc: '/images/tech/hero.webp',
    mobileSrc: '/images/tech/hero-mobile.jpg',
    mobileWebpSrc: '/images/tech/hero-mobile.webp',
    alt: 'Developer relaxing on a sofa with a laptop',
  },
};

/** The OpenAI payment notification floating over the hero image. */
export const techHeroTransaction = {
  label: 'Open AI',
  amount: '- 89.00 $',
  logo: '/images/tech/openai.png',
};

export const techOfferBanner = {
  text: 'Get 50% cashback on your first ChatGPT or Claude payment with your nsave card, up to $5.',
  learnMore: 'Learn more',
};

/** "Learn more" popup for the cashback offer (Figma 1382:9943). */
export const techOfferModal: {
  title: string;
  body: string;
  howItWorksTitle: string;
  steps: Array<{ icon: IconName; text: string }>;
  closingTitle: string;
  closingBody: string;
  cta: { label: string; href: string };
  terms: string;
} = {
  title: 'Your AI subscriptions, with a little extra back.',
  body: 'Get 50% cashback on your first eligible ChatGPT or Claude payment with your nsave card, up to $5. Pay directly through OpenAI or Anthropic until 30 November 2026 to qualify. Only your first transaction across the two platforms is eligible.',
  howItWorksTitle: 'How it works',
  steps: [
    { icon: 'card', text: 'Get your nsave card.' },
    { icon: 'sparkle', text: 'Make your first eligible ChatGPT or Claude payment.' },
    { icon: 'dollar', text: 'Get 50% cashback, up to $5.' },
  ],
  closingTitle: 'Ready to get more from your AI tools?',
  closingBody: 'Get your nsave card and make your first eligible payment to unlock cashback.',
  cta: { label: 'Get your nsave card', href: getAppCta.href },
  terms: 'Terms and Conditions apply',
};

export const techIdeasSection: TextSectionContent = {
  title: 'A better way to bring\nideas to life',
  body: 'From AI-powered workflows to creative software and development platforms, technology shapes how we work and what we create. nsave brings convenience and flexibility to your digital spending, helping you stay focused on the tools and projects that matter most.',
  bordered: true,
};

/** No image: the page renders the coded transactions panel in its place. */
export const techPromptsToProjects: Omit<FeatureBlockContent, 'image'> = {
  title: 'Prompts to Projects',
  rows: [
    {
      icon: 'sparkle',
      title: 'AI tools and subscriptions',
      description:
        'Pay for ChatGPT, Claude, and other AI subscriptions that support your research, coding, writing, and daily work.',
    },
    {
      icon: 'edit',
      title: 'Design and development',
      description:
        'Use your nsave card for tools like Figma, Adobe, and GitHub, from individual subscriptions to project essentials.',
    },
    {
      icon: 'monitor',
      title: 'Cloud and productivity',
      description:
        'Cover software, cloud services, and productivity platforms that help you manage projects and keep work moving.',
    },
  ],
};

/** Logo sizes are design pixels on the 51.9px tile; Cursor's logo is the whole tile. */
export const techCardTransactions: CardTransaction[] = [
  {
    merchant: 'Cursor',
    amount: '- 30.00 $',
    logo: '/images/tech/cursor.png',
    logoWidth: 51.9,
    logoHeight: 51.9,
  },
  {
    merchant: 'GitHub Copilot',
    amount: '- 100.00 $',
    logo: '/images/tech/github-copilot.png',
    logoWidth: 40,
    logoHeight: 31.65,
  },
  {
    // The Figma frame shows the OpenAI mark on this row.
    merchant: 'Adobe',
    amount: '- 69.99 $',
    logo: '/images/tech/openai.png',
    logoWidth: 35.51,
    logoHeight: 35.51,
  },
  {
    merchant: 'Figma',
    amount: '- 90.00 $',
    logo: '/images/tech/figma.png',
    logoWidth: 21.33,
    logoHeight: 32,
  },
];

export const techDigitalSpending: FeatureBlockContent = {
  title: 'Digital Spending',
  image: {
    src: '/images/tech/digital-spending.jpg',
    webpSrc: '/images/tech/digital-spending.webp',
    mobileSrc: '/images/tech/digital-spending-mobile.jpg',
    mobileWebpSrc: '/images/tech/digital-spending-mobile.webp',
    alt: 'Hands holding a phone showing the red nsave card in the app',
  },
  rows: [
    {
      icon: 'dollar-coin',
      title: 'Pay directly in USD',
      description:
        'Pay for USD-billed software directly from your USD card balance, without converting your dollars to local currency and back again.',
    },
    {
      icon: 'refresh',
      title: 'Manage recurring payments',
      description:
        'Use your nsave card for monthly and annual software subscriptions, from AI tools to development platforms.',
    },
    {
      icon: 'phone-vibrate',
      title: 'Pay with Apple Pay or Google Pay',
      description:
        'Add your nsave card to your digital wallet for convenient contactless payments, wherever supported.',
    },
  ],
};

export const techCashback: {
  title: string;
  body: string;
  cta: { label: string; href: string };
  image: ImageRef;
  /** `nsaveTile`: the cashback toast shows the nsave mark on the brand red tile. */
  transactions: Array<{ label: string; amount: string; logo: string; nsaveTile?: boolean }>;
} = {
  title: '50% cashback on\nClaude and ChatGPT',
  body: 'Your next AI subscription comes with a little extra. Get 50% cashback on your first eligible ChatGPT or Claude payment with your nsave card, up to $5.',
  cta: { label: 'Get your nsave card', href: getAppCta.href },
  image: {
    src: '/images/tech/cashback.jpg',
    webpSrc: '/images/tech/cashback.webp',
    mobileSrc: '/images/tech/cashback-mobile.jpg',
    mobileWebpSrc: '/images/tech/cashback-mobile.webp',
    alt: 'Gold nsave Visa Infinite card standing on desert rocks',
  },
  transactions: [
    { label: 'Anthropic Claude', amount: '- 10.00 $', logo: '/images/tech/anthropic.png' },
    { label: 'Cashback', amount: '+ 5.00 $', logo: '/images/tech/nsave-mark.png', nsaveTile: true },
  ],
};

// TODO: draft answers, not yet approved. The Figma frames only carry the questions.
export const techFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'Can I pay for subscriptions billed in USD?',
      answer:
        'Yes. Your nsave card spends from your USD balance, so USD-billed subscriptions are paid in dollars without converting to local currency first.',
    },
    {
      question: 'Are there any fees for using my nsave card?',
      answer:
        'Check the pricing page in the nsave app for the current card fees that apply to your account and country.',
    },
    {
      question: 'Can I manage my card through the nsave app?',
      answer:
        'Yes. Freeze or unfreeze your card, view your card details, track payments and add the card to Apple Pay or Google Pay, all from the nsave app.',
    },
    {
      question: 'How does the 50% AI tools cashback work?',
      answer:
        'Pay for your first eligible ChatGPT or Claude subscription with your nsave card and get 50% of that payment back, up to $5.',
    },
    {
      question: 'Do payments through the App Store or Google Play qualify?',
      answer:
        'The offer applies to eligible ChatGPT or Claude payments made with your nsave card. Payments billed by the App Store or Google Play are charged by Apple or Google, so they may not qualify.',
    },
    {
      question: 'Can I receive cashback on both ChatGPT and Claude?',
      answer:
        'The cashback applies to your first eligible ChatGPT or Claude payment made with your nsave card, up to $5.',
    },
  ],
};
